<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import Input from '../../../../../components-next/input/Input.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'exemplo@empresa.com',
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
  maxLength: {
    type: Number,
    default: 100, // Máximo de 100 caracteres para email
  }
});

const emit = defineEmits(['update:modelValue', 'validation']);

const emailValue   = ref(props.modelValue);
const isValid      = ref(!props.required);
const isTouched    = ref(false);
const internalError= ref('');
const showSuggestions = ref(false);
const selectedSuggestionIndex = ref(-1);
const suggestionsDropdown = ref(null);

// Regex Unicode para permitir letras (com acentos), números, . _ % + - no local, e domínio com acentos também
const emailRegex = /^[\p{L}\p{N}._%+-]+@[\p{L}\p{N}.-]+\.[\p{L}]{2,}$/u;

// Domínios comuns brasileiros e internacionais
const commonDomains = [
  'gmail.com',
  'hotmail.com',
  'outlook.com',
  'yahoo.com',
  'yahoo.com.br',
  'terra.com.br',
  'uol.com.br',
  'bol.com.br',
  'ig.com.br',
  'globo.com',
  'icloud.com',
  'live.com',
  'msn.com'
];

// Computed para gerar sugestões
const emailSuggestions = computed(() => {
  if (!emailValue.value || !emailValue.value.includes('@')) {
    return [];
  }
  
  const parts = emailValue.value.split('@');
  const localPart = parts[0];
  const domainPart = parts[1] || '';
  
  if (!localPart || domainPart.includes('.')) {
    return [];
  }
  
  // Filtrar domínios que começam com o que foi digitado
  const filtered = commonDomains.filter(domain => 
    domain.toLowerCase().startsWith(domainPart.toLowerCase())
  );
  
  // Retornar no máximo 5 sugestões
  return filtered.slice(0, 5).map(domain => `${localPart}@${domain}`);
});

function validateEmail(email) {

  const trimmed = email.trim();

  if (!trimmed) {
    if (props.required) {
      internalError.value = 'O email de suporte é obrigatório';
      return false;
    }
    internalError.value = '';
    return true;
  }

  if (trimmed.length > props.maxLength) {
    internalError.value = `O email não pode ter mais de ${props.maxLength} caracteres`;
    return false;
  }

  if (!emailRegex.test(trimmed)) {
    internalError.value = 'Por favor, insira um email válido no formato usuario@dominio.com';
    return false;
  }

  internalError.value = '';
  return true;
}

function validateAndEmit() {
  isValid.value = validateEmail(emailValue.value);
  emit('validation', isValid.value);
  return isValid.value;
}

function handleInput(e) {
  // remove espaços à direita e esquerda
  const raw = e.target.value;
  const trimmed = raw.replace(/^\s+|\s+$/g, '').toLowerCase();
  emailValue.value = trimmed;
  emit('update:modelValue', trimmed);

  // Mostrar sugestões quando tem @ mas não tem domínio completo
  showSuggestions.value = trimmed.includes('@') && !trimmed.split('@')[1]?.includes('.');
  selectedSuggestionIndex.value = -1;

  // Sempre validar durante a digitação quando o campo tem conteúdo
  if (trimmed.length > 0) {
    isTouched.value = true;
    validateAndEmit();
  } else if (isTouched.value) {
    // Se já foi tocado e agora está vazio, continua validando
    validateAndEmit();
  }
}

function handleBlur(e) {
  isTouched.value = true;
  // Delay hiding suggestions to allow click events on suggestions
  setTimeout(() => {
    showSuggestions.value = false;
  }, 200);
  validateAndEmit();
}

function handleFocus() {
  if (emailValue.value.includes('@') && !emailValue.value.split('@')[1]?.includes('.')) {
    showSuggestions.value = true;
  }
}

function selectSuggestion(suggestion) {
  emailValue.value = suggestion;
  emit('update:modelValue', suggestion);
  showSuggestions.value = false;
  selectedSuggestionIndex.value = -1;
  validateAndEmit();
}

function handleKeyDown(e) {
  if (!showSuggestions.value || emailSuggestions.value.length === 0) return;
  
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      selectedSuggestionIndex.value = Math.min(
        selectedSuggestionIndex.value + 1,
        emailSuggestions.value.length - 1
      );
      scrollToSelectedItem();
      break;
      
    case 'ArrowUp':
      e.preventDefault();
      selectedSuggestionIndex.value = Math.max(selectedSuggestionIndex.value - 1, -1);
      scrollToSelectedItem();
      break;
      
    case 'Enter':
      if (selectedSuggestionIndex.value >= 0) {
        e.preventDefault();
        selectSuggestion(emailSuggestions.value[selectedSuggestionIndex.value]);
      }
      break;
      
    case 'Escape':
      showSuggestions.value = false;
      selectedSuggestionIndex.value = -1;
      break;
  }
}

// Função para fazer scroll automático até o item selecionado
function scrollToSelectedItem() {
  nextTick(() => {
    if (!suggestionsDropdown.value || selectedSuggestionIndex.value < 0) return;
    
    const dropdown = suggestionsDropdown.value;
    const items = dropdown.querySelectorAll('li');
    const selectedItem = items[selectedSuggestionIndex.value];
    
    if (selectedItem) {
      // Calcula a posição do item em relação ao container
      const itemTop = selectedItem.offsetTop;
      const itemBottom = itemTop + selectedItem.offsetHeight;
      const scrollTop = dropdown.scrollTop;
      const dropdownHeight = dropdown.clientHeight;
      
      // Se o item está abaixo da área visível
      if (itemBottom > scrollTop + dropdownHeight) {
        dropdown.scrollTop = itemBottom - dropdownHeight;
      }
      // Se o item está acima da área visível
      else if (itemTop < scrollTop) {
        dropdown.scrollTop = itemTop;
      }
    }
  });
}

// Sincroniza mudanças externas
watch(() => props.modelValue, newVal => {
  if (newVal !== emailValue.value) {
    emailValue.value = newVal;
    if (isTouched.value) validateAndEmit();
  }
});

// Handle click outside to close suggestions
function handleClickOutside(e) {
  const emailInputWrapper = e.target.closest('.email-input-wrapper');
  if (!emailInputWrapper) {
    showSuggestions.value = false;
    selectedSuggestionIndex.value = -1;
  }
}

// valida no mount para inicializar o estado
onMounted(() => {
  isValid.value = validateEmail(emailValue.value);
  emit('validation', isValid.value);
  
  // Add click outside listener
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  // Remove click outside listener
  document.removeEventListener('click', handleClickOutside);
});

const errorMessage = computed(() => {
  if (!isTouched.value) return '';
  return props.error || internalError.value;
});
</script>

<template>
  <div class="relative email-input-wrapper flex flex-col gap-1.5">
    <div class="relative" :class="{ 'input-with-suggestions': showSuggestions && emailSuggestions.length > 0 }">
      <Input
        :modelValue="emailValue"
        :placeholder="placeholder"
        type="email"
        :required="required"
        :disabled="disabled"
        :error="errorMessage"
        :class="{
          'rounded-b-none': showSuggestions && emailSuggestions.length > 0
        }"
        @input="handleInput"
        @blur="handleBlur"
        @focus="handleFocus"
        @keydown="handleKeyDown"
        @update:modelValue="val => emit('update:modelValue', val)"
      />
      
      <!-- Sugestões de domínios -->
      <div 
        v-if="showSuggestions && emailSuggestions.length > 0"
        ref="suggestionsDropdown"
        class="absolute z-50 w-full top-full bg-n-slate-1 border border-t-0 border-n-brand dark:border-n-brand rounded-b-lg shadow-lg max-h-48 overflow-y-auto suggestions-dropdown"
        @mousedown.prevent
      >
      <ul class="py-1">
        <li
          v-for="(suggestion, index) in emailSuggestions"
          :key="suggestion"
          class="px-4 py-2 cursor-pointer text-sm transition-all duration-150"
          :class="{
            'selected-suggestion bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200': index === selectedSuggestionIndex,
            'hover:bg-n-slate-50 dark:hover:bg-n-slate-700 text-n-slate-700 dark:text-n-slate-300': index !== selectedSuggestionIndex,
            'bg-white dark:bg-n-slate-800': index !== selectedSuggestionIndex
          }"
          @mousedown.prevent="selectSuggestion(suggestion)"
          @mouseenter="selectedSuggestionIndex = index"
        >
          <span class="font-medium">{{ suggestion.split('@')[0] }}</span>
          <span :class="index === selectedSuggestionIndex ? 'text-primary-600 dark:text-primary-400' : 'text-n-slate-500 dark:text-n-slate-400'">@{{ suggestion.split('@')[1] }}</span>
        </li>
      </ul>
      <div class="px-4 py-2 text-xs text-n-slate-500 dark:text-n-slate-400 border-t border-n-slate-200 dark:border-n-slate-700">
        Use ↑↓ para navegar, Enter para selecionar
      </div>
    </div>
    </div>
    
    <div class="h-5 text-n-ruby-9 text-sm">
      <span v-if="errorMessage && isTouched">{{ errorMessage }}</span>
      <span v-else-if="!isTouched && required" class="text-n-slate-10 text-xs">
        Digite o email e veja sugestões de domínios comuns
      </span>
    </div>
  </div>
</template>

<style scoped>
/* Destaque visual para item selecionado no dropdown */
.selected-suggestion {
  background: linear-gradient(90deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%);
  border-left: 3px solid rgb(59 130 246);
  padding-left: calc(1rem - 3px);
}

.input-with-suggestions::before,
.input-with-suggestions::after {
  content: "";
  position: absolute;
  top: 50%;
  height: 50%;
  width: 1px;
  background-color: rgb(39 129 246); /* n-slate-200 */
  z-index: 10;
}

.input-with-suggestions::before {
  left: 0;
}

.input-with-suggestions::after {
  right: 0;
}

/* Dark mode */
@media (prefers-color-scheme: dark) {
  .input-with-suggestions::before,
  .input-with-suggestions::after {
    background-color: rgb(39 129 246); /* n-brand */
  }
}

/* Custom scrollbar styles */
.suggestions-dropdown {
  scrollbar-width: thin;
  scrollbar-color: rgb(203 213 225) transparent; /* n-slate-300 */
}

.suggestions-dropdown::-webkit-scrollbar {
  width: 8px;
}

.suggestions-dropdown::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 0 0 0.5rem 0; /* rounded-b-lg on right side only */
}

.suggestions-dropdown::-webkit-scrollbar-thumb {
  background-color: rgb(203 213 225); /* n-slate-300 */
  border-radius: 4px;
}

.suggestions-dropdown::-webkit-scrollbar-thumb:hover {
  background-color: rgb(148 163 184); /* n-slate-400 */
}

/* Dark mode scrollbar */
@media (prefers-color-scheme: dark) {
  .suggestions-dropdown {
    scrollbar-color: rgb(71 85 105) transparent; /* n-slate-600 */
  }
  
  .suggestions-dropdown::-webkit-scrollbar-thumb {
    background-color: rgb(71 85 105); /* n-slate-600 */
  }
  
  .suggestions-dropdown::-webkit-scrollbar-thumb:hover {
    background-color: rgb(100 116 139); /* n-slate-500 */
  }
}
</style>